import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useLocation, useSearchParams } from 'react-router-dom'
import useURLState from '../../hooks/useURLState'
import {
  DASHBOARD_URL_CONFIG,
  DASHBOARD_VIEW_KEYS,
  sanitizeDashboardView,
  rangeOf,
  toUrlValues,
} from './dashboardView'

const sameView = (a, b) => DASHBOARD_VIEW_KEYS.every((key) => a[key] === b[key])

const useDashboardUrlView = ({ metricOptions } = {}) => {
  const { pathname } = useLocation()
  const [searchParams] = useSearchParams()
  const {
    period, from, to, group, affiliate, metric, view: details, day, page, limit, sort, setMultiple,
  } = useURLState(DASHBOARD_URL_CONFIG)

  const view = useMemo(
    () => sanitizeDashboardView(
      { period, from, to, group, affiliate, metric, view: details, day, page, limit, sort },
      { metricOptions },
    ),
    [period, from, to, group, affiliate, metric, details, day, page, limit, sort, metricOptions],
  )

  const { from: rangeFrom, to: rangeTo } = view
  const customDateRange = useMemo(() => rangeOf({ from: rangeFrom, to: rangeTo }), [rangeFrom, rangeTo])

  const hasView = DASHBOARD_VIEW_KEYS.some((key) => searchParams.has(key))

  const pending = useRef(null)

  useEffect(() => {
    pending.current = null
  }, [searchParams])

  const update = useCallback((transition) => {
    if (window.location.pathname !== pathname) return
    const current = pending.current || view
    const next = transition(current)
    if (sameView(next, current)) return
    pending.current = next
    setMultiple(toUrlValues(next), { replace: true })
  }, [view, setMultiple, pathname])

  return { view, customDateRange, update, hasView }
}

export default useDashboardUrlView
